// src/components/Noticias/CarruselNoticias.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Container, Carousel, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { fetchNoticias } from '../../redux/noticiasSlice';
import { parseYouTube, youtubeThumb } from '../../utils/youtube';
import YouTubeEmbed from '../YouTubeEmbed';
import '../../styles/carruselNoticias.css';

const MAX_POR_CARRUSEL = 4;

function comoArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.noticias)) return payload.noticias;
  return [];
}

function tiempo(n) {
  const f = n?.fecha_publicacion || n?.createdAt || n?.updatedAt;
  const t = f ? new Date(f).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

function tieneMultimedia(n) {
  const tieneVideo = !!parseYouTube(n?.video_url);
  const tieneImagen = !!(n?.imagen_url && String(n.imagen_url).trim());
  return { tieneVideo, tieneImagen, esMultimedia: tieneVideo || tieneImagen };
}

function TarjetaNoticia({ noticia }) {
  const { tieneVideo, tieneImagen } = tieneMultimedia(noticia);
  const yt = tieneVideo ? parseYouTube(noticia.video_url) : null;
  const thumb = tieneImagen ? noticia.imagen_url : yt ? youtubeThumb(yt.id) : null;
  const resumen =
    (noticia.descripcion || '').length > 120
      ? `${noticia.descripcion.slice(0, 120)}…`
      : noticia.descripcion || '';

  return (
    <Card className="carrusel-noticia-card h-100">
      {tieneVideo ? (
        // Con video: reproductor inline (lazy-load, arranca al hacer clic).
        <div className="carrusel-noticia-video-wrap">
          <YouTubeEmbed url={noticia.video_url} titulo={noticia.titulo || 'Video'} autoPlayDelay={12} />
        </div>
      ) : (
        thumb && (
          <div className="carrusel-noticia-img-wrap">
            <img
              src={thumb}
              alt={noticia.titulo || 'Noticia'}
              className="carrusel-noticia-img"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )
      )}
      <Card.Body className="d-flex flex-column">
        <Card.Title className="carrusel-noticia-titulo">
          {noticia.titulo || 'Sin título'}
        </Card.Title>
        <Card.Text className="carrusel-noticia-resumen flex-grow-1">{resumen}</Card.Text>
        <Button as={Link} to="/noticias" variant="outline-danger" size="sm" className="align-self-center mt-2">
          Ver más
        </Button>
      </Card.Body>
    </Card>
  );
}

function agruparEnSlides(items, porSlide) {
  const slides = [];
  for (let i = 0; i < items.length; i += porSlide) {
    slides.push(items.slice(i, i + porSlide));
  }
  return slides;
}

function CarruselSeccion({ titulo, icono, noticias, itemsPorSlide }) {
  if (!noticias || noticias.length === 0) return null;
  const slides = agruparEnSlides(noticias, itemsPorSlide);

  return (
    <div className="carrusel-seccion mb-5">
      <h2 className="carrusel-seccion-titulo">
        {icono && <i className={`bi ${icono} me-2`} aria-hidden="true"></i>}
        {titulo}
      </h2>
      {slides.length === 1 ? (
        <div className="carrusel-grid">
          {slides[0].map((n) => (<TarjetaNoticia key={n.id} noticia={n} />))}
        </div>
      ) : (
        <Carousel interval={null} indicators variant="dark" className="carrusel-noticias">
          {slides.map((grupo, idx) => (
            <Carousel.Item key={idx}>
              <div className="carrusel-grid">
                {grupo.map((n) => (<TarjetaNoticia key={n.id} noticia={n} />))}
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      )}
    </div>
  );
}

export default function CarruselNoticias() {
  const dispatch = useDispatch();
  const [errorLocal, setErrorLocal] = useState(false);
  const noticiasState = useSelector((s) => s.noticias?.noticias);
  const loading = useSelector((s) => s.noticias?.loading);

  useEffect(() => {
    Promise.resolve(dispatch(fetchNoticias())).catch(() => setErrorLocal(true));
  }, [dispatch]);

  const { conMultimedia, soloTexto } = useMemo(() => {
    const todas = comoArray(noticiasState);
    const ordenadas = [...todas].sort((a, b) => tiempo(b) - tiempo(a));
    const conMultimedia = [];
    const soloTexto = [];
    for (const n of ordenadas) {
      if (tieneMultimedia(n).esMultimedia) conMultimedia.push(n);
      else soloTexto.push(n);
    }
    return {
      conMultimedia: conMultimedia.slice(0, MAX_POR_CARRUSEL),
      soloTexto: soloTexto.slice(0, MAX_POR_CARRUSEL),
    };
  }, [noticiasState]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="danger" role="status" />
        <p className="mt-2 text-muted mb-0">Cargando noticias…</p>
      </Container>
    );
  }

  if (errorLocal) {
    return (
      <Container className="py-4">
        <Alert variant="light" className="text-center border">
          No pudimos cargar las noticias en este momento.{' '}
          <Link to="/noticias">Ver todas las noticias</Link>
        </Alert>
      </Container>
    );
  }

  const hayAlgo = conMultimedia.length > 0 || soloTexto.length > 0;
  if (!hayAlgo) return null;

  return (
    <section className="home-noticias py-5" aria-label="Noticias recientes">
      <Container>
        <CarruselSeccion titulo="Destacadas" icono="bi-star-fill" noticias={conMultimedia} itemsPorSlide={2} />
        <CarruselSeccion titulo="Más noticias" icono="bi-newspaper" noticias={soloTexto} itemsPorSlide={3} />

        <div className="text-center mt-4">
          <Button as={Link} to="/noticias" variant="danger" size="lg">
            Ver todas las noticias
          </Button>
        </div>
      </Container>
    </section>
  );
}